import React, {
  useState,
  useCallback,
  useEffect,
  type Dispatch,
  type SetStateAction,
  type JSX
} from 'react';
import { Direction, useOnSwipe } from 'shared/hooks';
import { CarouselControls } from './CarouselControls';
import { CarouselSlides } from './CarouselSlides';
import styles from './Carousel.module.scss';

export type CarouselProps = {
  enableControls?: boolean;
  enableLoop?: boolean;
  initialSlide?: number;
  selectedSlide?: number;
  setSelectedSlide?: Dispatch<SetStateAction<number>>;
  slides: JSX.Element[];
  onClick?: (analyticsType: string) => void;
  isPdp?: boolean;
};

export const Carousel = ({
  enableControls = true,
  enableLoop = true,
  isPdp = false,
  initialSlide = 0,
  onClick = () => {},
  // NOTE: reason for both parent set state and internal set state is to be able to manage the state
  // even if parent doesn't care about the selected state without passing extra props
  selectedSlide: parentSelectedSlide,
  setSelectedSlide: setParentSelectedSlide,
  slides = []
}: CarouselProps) => {
  const [selectedSlide, setSelectedSlide] = useState(initialSlide);

  const getNextSlide = useCallback(
    (prevSelectedSlide: number) => {
      if (selectedSlide !== slides.length - 1) return prevSelectedSlide + 1;
      if (enableLoop) return 0;

      return selectedSlide;
    },
    [enableLoop, selectedSlide, slides.length]
  );

  const getPreviousSlide = useCallback(
    (prevSelectedSlide: number) => {
      if (selectedSlide !== 0) return prevSelectedSlide - 1;
      if (enableLoop) return slides.length - 1;

      return selectedSlide;
    },
    [enableLoop, selectedSlide, slides.length]
  );

  const handleNextSlide = useCallback(() => {
    onClick(`arrow`);

    setSelectedSlide(getNextSlide);
    if (setParentSelectedSlide) setParentSelectedSlide(getNextSlide);
  }, [getNextSlide, setParentSelectedSlide, onClick]);

  const handlePreviousSlide = useCallback(() => {
    onClick(`arrow`);

    setSelectedSlide(getPreviousSlide);
    if (setParentSelectedSlide) setParentSelectedSlide(getPreviousSlide);
  }, [getPreviousSlide, setParentSelectedSlide, onClick]);

  const touchEvents = useOnSwipe(Direction.horizontal, {
    onSwipeLeft: () => {
      handleNextSlide();
    },
    onSwipeRight: () => {
      handlePreviousSlide();
    }
  });

  // syncing the internal state if selected from parent
  useEffect(() => {
    if (parentSelectedSlide !== undefined) setSelectedSlide(parentSelectedSlide);
  }, [parentSelectedSlide]);

  return (
    <div className={styles.container} aria-label="Carousel" role="group">
      {enableControls && (
        <CarouselControls
          handleNextSlide={handleNextSlide}
          handlePreviousSlide={handlePreviousSlide}
          showNextButton={enableLoop || selectedSlide !== slides.length - 1}
          showPreviousButton={enableLoop || selectedSlide !== 0}
        />
      )}
      <CarouselSlides
        touchEvents={touchEvents}
        enableLoop={enableLoop}
        selectedSlide={selectedSlide}
        slides={slides}
        isPdp={isPdp}
      />
    </div>
  );
};
